'use client'

/**
 * La pantalla que ve el cajero cuando algo revienta de verdad.
 *
 * El 2026-08-21 lo que vio fue la pantalla cruda del navegador: fondo blanco,
 * un triangulo gris y «This page couldn't load. A server error occurred». En
 * ingles, sin decir que hacer, y con un codigo de error que no le sirve a nadie.
 * El cajero no supo si el pedido se habia guardado, si tenia que repetirlo, ni a
 * quien avisarle. Se puso a escribir comandas a mano.
 *
 * Esta pantalla contesta esas tres preguntas. Y avisa sola, para que el dueno
 * no dependa de que alguien se acuerde de contarlo.
 *
 * `global-error` reemplaza el layout entero, asi que lleva sus propias etiquetas
 * html y body — no es un olvido.
 */
import { useEffect, useState } from 'react'
import { esCaidaDeRed } from '@/shared/lib/caidaDeRed'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // Misma distincion que en `(main)/error.tsx`: decirle a alguien que revise
  // si su pedido se guardo a medias es FALSO cuando lo que fallo fue la red —
  // sin conexion la peticion no salio. Ver `shared/lib/caidaDeRed.ts`.
  const [deRed] = useState(() =>
    esCaidaDeRed(error, typeof navigator === 'undefined' ? undefined : navigator.onLine),
  )

  useEffect(() => {
    // Avisar de una vez. El `keepalive` hace que salga aunque el cajero cierre
    // la pestania al segundo siguiente, que es lo mas probable.
    try {
      // Una sola vez por firma y por pestania. Si el error es determinista, el
      // cajero va a tocar «Intentar de nuevo» varias veces y esta pantalla se
      // volveria a montar en cada intento: sin este freno, un solo problema
      // mandaria un reporte por cada toque.
      const firma = 'dw_pantalla_caida_' + (error?.digest ?? (error?.message ?? '').slice(0, 80))
      try {
        if (sessionStorage.getItem(firma)) return
        sessionStorage.setItem(firma, '1')
      } catch {
        // Sin sessionStorage se reporta igual: mejor un duplicado que un silencio.
      }

      void fetch('/api/reportar-error-navegador', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          mensaje:
            (deRed ? 'SIN CONEXION: ' : 'PANTALLA CAIDA: ') + (error?.message ?? 'sin mensaje'),
          ruta: window.location.pathname + window.location.search,
          traza: error?.stack?.slice(0, 1500),
        }),
      }).catch(() => {})
    } catch {
      // Esta pantalla existe para ayudar; nunca puede fallar por su cuenta.
    }
  }, [error, deRed])

  return (
    // `global-error` reemplaza el arbol ENTERO, y con el se va el viewport que
    // Next pone por su cuenta. Sin estas dos etiquetas, el celular del cajero
    // asume una pantalla de 980px y encoge todo: el titulo de 26px se ve a 11 y
    // los botones quedan del tamano de una una. La pantalla de emergencia seria
    // ilegible justo en la emergencia. Y sin charSet, los acentos se rompen.
    <html lang="es">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Se atoró la pantalla · DeliWins</title>
      </head>
      <body
        style={{
          margin: 0,
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff8e7',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: 20,
        }}
      >
        <div
          style={{
            background: '#fff',
            border: '4px solid #1a1a1a',
            borderRadius: 20,
            padding: 28,
            maxWidth: 460,
            width: '100%',
            textAlign: 'center',
            boxShadow: '8px 8px 0 #1a1a1a',
          }}
        >
          <p style={{ fontSize: 52, margin: 0, lineHeight: 1 }}>{deRed ? '📡' : '🔧'}</p>

          <h1
            style={{
              fontSize: 26,
              fontWeight: 900,
              color: '#da291c',
              margin: '14px 0 0',
              textTransform: 'uppercase',
              letterSpacing: '-0.5px',
            }}
          >
            {deRed ? 'Se fue la conexión' : 'Se atoró la pantalla'}
          </h1>

          <p style={{ fontSize: 16, color: '#1a1a1a', marginTop: 12, lineHeight: 1.5 }}>
            {deRed
              ? 'No es la aplicación: el internet se cortó. Revisa el WiFi del local y vuelve a intentar.'
              : 'No fue algo que hiciste mal. Ya le avisamos a soporte automáticamente.'}
          </p>

          <div
            style={{
              background: '#fff8e7',
              border: '2px solid #1a1a1a',
              borderRadius: 12,
              padding: 14,
              marginTop: 18,
              textAlign: 'left',
            }}
          >
            <p style={{ fontSize: 14, fontWeight: 900, color: '#1a1a1a', margin: 0 }}>
              {deRed ? '📋 Tu pedido está a salvo' : '⚠️ Antes de recapturar, REVISA'}
            </p>
            <p style={{ fontSize: 14, color: '#1a1a1a', margin: '6px 0 0', lineHeight: 1.5 }}>
              {deRed ? (
                <>
                  Sin conexión no se alcanzó a mandar nada, así que{' '}
                  <strong>no se guardó a medias</strong>. Cuando vuelva la señal, vuelve a
                  entrar y sigue donde te quedaste.
                </>
              ) : (
                <>
                  Busca el pedido en la <strong>lista de pedidos</strong>. Puede estar completo,
                  a medias o no existir. Recaptúralo solo si <strong>no aparece</strong>; si
                  aparece incompleto, corrígelo.
                </>
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={() => reset()}
            style={{
              marginTop: 20,
              width: '100%',
              padding: '16px 20px',
              borderRadius: 999,
              border: '3px solid #1a1a1a',
              background: '#ffc72c',
              color: '#1a1a1a',
              fontSize: 15,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
            }}
          >
            🔄 Intentar de nuevo
          </button>

          {/* A la LISTA DE PEDIDOS, que es donde tiene que ir a verificar si su
              pedido quedo. Mandarlo al inicio lo obligaria a navegar de mas
              justo cuando tiene gente esperando. */}
          <button
            type="button"
            onClick={() => {
              window.location.href = '/dashboard/orders'
            }}
            style={{
              marginTop: 10,
              width: '100%',
              padding: '14px 20px',
              borderRadius: 999,
              border: '3px solid #1a1a1a',
              background: '#fff',
              color: '#1a1a1a',
              fontSize: 14,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
            }}
          >
            📋 Ver la lista de pedidos
          </button>

          {error?.digest ? (
            <p style={{ fontSize: 11, color: '#999', marginTop: 16, marginBottom: 0 }}>
              Clave para soporte: {error.digest}
            </p>
          ) : null}
        </div>
      </body>
    </html>
  )
}
